import { ConnectionEndpointGroup } from "../../../src/connectionAnalyser/ConnectionEndpointGroup";
import { C1_ELO_PLUG_TYPE_LABELS } from "../C1_uiLabels";
import { itx } from "../../../src/translationItx/itx";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";

import { WallOutletSvg, wallOutletLayout, type WallOutletSlotType } from "../frontViews/WallOutletFrontview";
import C1_EloItem from "../model/C1_EloItem";
import { C1_EloGroup } from "../model/C1_EloGroup";
import { commitOwnerEloTree, eloRootAndOwner } from "../model/C1_eloOwnerEditing";
import {
  appendRepeatedItem,
  nextRepeatedItemDefinitions,
  repeatItemCount,
  repeatItemLimit,
  updateEloItemDetails,
} from "../model/C1_eloTreeEditing";
import {
  C1_EloItem_PlugType,
  C1_EloItem_Type,
  type C1_EloItem_Definition,
  type C1_LegacyEloItemTreeData,
} from "../types/C1_EloTypes";
import c1_state from "../model/C1_State";
import { C1_EloAddBadge } from "./C1_EloAddBadge";

type InlineEditorGeometry = { x: number; y: number; width: number; height: number };
type SlotTypeFlyout = { childId: string; anchor: SVGGElement; left: number; top: number };

type C1_EloInlineEditorProps = {
  item: C1_EloGroup;
  definition: C1_EloItem_Definition;
  geometry: InlineEditorGeometry;
  groupSelected: boolean;
  selectedChildId?: string;
  showAdd: boolean;
  interactionOnly?: boolean;
  onSlotHoverChange?: (childId?: string) => void;
  onAddHover?: () => void;
  onSlotPointerDown?: (event: React.PointerEvent<SVGRectElement>, child: C1_EloItem) => void;
  onSlotClick?: (child: C1_EloItem) => void;
  contentRotation?: number;
};

function plugTypeSlot(type: C1_EloItem_PlugType): WallOutletSlotType {
  if (type.startsWith(C1_EloItem_PlugType.V230Switch)) return "schukoSwitched";
  switch (type) {
    case C1_EloItem_PlugType.LAN: return "lan";
    case C1_EloItem_PlugType.LAN2: return "lan2";
    case C1_EloItem_PlugType.USB: return "usb";
    case C1_EloItem_PlugType.TV: return "tv";
    default: return "schuko";
  }
}

function plugTypeLabel(type: C1_EloItem_PlugType): string | undefined {
  if (type === C1_EloItem_PlugType.V230Switch1) return "1";
  if (type === C1_EloItem_PlugType.V230Switch2) return "2";
  if (type === C1_EloItem_PlugType.V230Switch3) return "3";
  return undefined;
}

function runTreeEdit(
  item: C1_EloItem,
  edit: (root: C1_LegacyEloItemTreeData) => C1_LegacyEloItemTreeData,
  selectedItemId = item.id,
) {
  const context = eloRootAndOwner(item);
  if (!context) return;
  const currentRoot = context.root.toLegacyTreeData();
  const nextRoot = edit(currentRoot);
  if (nextRoot === currentRoot) return;
  commitOwnerEloTree(context.owner, nextRoot, selectedItemId);
}

function appendRepeatedItemAndSelect(item: C1_EloItem, definition: C1_EloItem_Definition) {
  const context = eloRootAndOwner(item);
  if (!context) return;
  const previousIds = new Set(context.root.allItems.map((candidate) => candidate.id));
  const currentRoot = context.root.toLegacyTreeData();
  const nextRoot = appendRepeatedItem(
    currentRoot,
    item.id,
    definition,
    c1_state.project.equipmentStandard,
  );
  if (nextRoot === currentRoot) return;

  let addedItemId: string | undefined;
  const findAddedItem = (candidate: C1_LegacyEloItemTreeData) => {
    if (addedItemId) return;
    if (!previousIds.has(candidate.id)) {
      addedItemId = candidate.id;
      return;
    }
    candidate.items.forEach(findAddedItem);
  };
  findAddedItem(nextRoot);
  commitOwnerEloTree(context.owner, nextRoot, addedItemId);
}

function displayedLayout(geometry: InlineEditorGeometry, slotCount: number, includeAddSlot: boolean) {
  const source = wallOutletLayout(slotCount + (includeAddSlot ? 1 : 0));
  const scale = Math.min(geometry.width / source.frameWidth, geometry.height / source.frameHeight);
  const offsetX = geometry.x + (geometry.width - source.frameWidth * scale) / 2;
  const offsetY = geometry.y + (geometry.height - source.frameHeight * scale) / 2;
  const map = (rect: typeof source.nextSlot) => ({
    x: offsetX + rect.x * scale,
    y: offsetY + rect.y * scale,
    width: rect.width * scale,
    height: rect.height * scale,
  });
  return {
    slots: source.slots.slice(0, slotCount).map(map),
    nextSlot: map(source.slots[slotCount] ?? source.nextSlot),
  };
}

function C1_PlugGroupInlineEditor({
  item,
  definition,
  geometry,
  groupSelected,
  selectedChildId,
  showAdd,
  interactionOnly = false,
  onSlotHoverChange,
  onAddHover,
  onSlotPointerDown,
  onSlotClick,
  contentRotation = 0,
}: C1_EloInlineEditorProps) {
  const [flyout, setFlyout] = useState<SlotTypeFlyout>();
  const [addHovered, setAddHovered] = useState(false);
  const flyoutRef = useRef<HTMLDivElement>(null);
  useEffect(() => setFlyout(undefined), [selectedChildId]);
  useEffect(() => {
    if (!flyout) return;
    const closeOnOutsidePointer = (event: PointerEvent) => {
      if (!flyoutRef.current?.contains(event.target as Node)) setFlyout(undefined);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setFlyout(undefined);
    };
    window.addEventListener("pointerdown", closeOnOutsidePointer);
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      window.removeEventListener("pointerdown", closeOnOutsidePointer);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [flyout?.childId]);
  useLayoutEffect(() => {
    const anchor = flyout?.anchor;
    const panel = flyoutRef.current;
    if (!anchor || !panel || !anchor.isConnected) return;
    const drawingArea = anchor.closest(".C1_drawingArea");
    const canvas = anchor.closest(".C1_canvas");
    if (!(drawingArea instanceof HTMLElement) || !(canvas instanceof HTMLElement)) return;

    const updatePlacement = () => {
      if (!anchor.isConnected) {
        setFlyout(undefined);
        return;
      }
      const anchorRect = anchor.getBoundingClientRect();
      const panelRect = panel.getBoundingClientRect();
      const drawingRect = drawingArea.getBoundingClientRect();
      const visibleLeft = Math.max(1, drawingRect.left + 1);
      const visibleRight = Math.min(window.innerWidth - 1, drawingRect.right - 1);
      const visibleTop = Math.max(1, drawingRect.top + 1);
      const visibleBottom = Math.min(window.innerHeight - 1, drawingRect.bottom - 1);
      const preferredLeft = anchorRect.left + anchorRect.width / 2 - panelRect.width / 2;
      const left = Math.max(visibleLeft, Math.min(preferredLeft, visibleRight - panelRect.width));
      const belowTop = anchorRect.bottom + 1;
      const top = belowTop + panelRect.height <= visibleBottom
        ? belowTop
        : Math.max(visibleTop, anchorRect.top - panelRect.height - 1);
      setFlyout((current) =>
        current && (current.left !== left || current.top !== top)
          ? { ...current, left, top }
          : current,
      );
    };

    updatePlacement();
    drawingArea.addEventListener("scroll", updatePlacement);
    window.addEventListener("resize", updatePlacement);
    const resizeObserver = new ResizeObserver(updatePlacement);
    resizeObserver.observe(drawingArea);
    resizeObserver.observe(canvas);
    resizeObserver.observe(panel);
    return () => {
      drawingArea.removeEventListener("scroll", updatePlacement);
      window.removeEventListener("resize", updatePlacement);
      resizeObserver.disconnect();
    };
  }, [flyout?.anchor, flyout?.childId]);
  const children = [...item.items].sort((left, right) => {
    const index = (definitionId: string) => Number(definitionId.match(/-(\d+)$/)?.[1] ?? 0);
    return index(left.definition?.definitionId ?? "") - index(right.definition?.definitionId ?? "");
  });
  const canAppend = repeatItemCount(definition, item.toLegacyTreeData()) < repeatItemLimit(definition);
  const showAddSlot = !interactionOnly && groupSelected && showAdd && canAppend;
  const addItemName = showAddSlot
    ? nextRepeatedItemDefinitions(definition, item.toLegacyTreeData())
      .map((child) => child.description[c1_state.language]).filter(Boolean).join(", ") || itx("toolBar.outletShort", "Steckdose")
    : itx("toolBar.outletShort", "Steckdose");
  const layout = displayedLayout(geometry, children.length, showAddSlot);
  const addAngle = contentRotation * Math.PI / 180;
  const addBadgeX = layout.nextSlot.x + layout.nextSlot.width / 2
    + layout.nextSlot.width / 2 * Math.cos(addAngle) - layout.nextSlot.height / 2 * Math.sin(addAngle);
  const addBadgeY = layout.nextSlot.y + layout.nextSlot.height / 2
    - layout.nextSlot.width / 2 * Math.sin(addAngle) - layout.nextSlot.height / 2 * Math.cos(addAngle);
  const controlSize = Math.max(7, Math.min(11, geometry.height * 0.42));

  const stopPointer = (event: React.PointerEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <>
      <g className="C1_eloInlineEditor" data-c1-svg-layer="elo-inline-editor">
        {children.map((child, index) => {
          const slot = layout.slots[index];
          const contentRotationRad = (contentRotation * Math.PI) / 180;
          const screenCornerOffsetX = slot.width * 0.25;
          const screenCornerOffsetY = -slot.height * 0.25;
          const buttonX =
            slot.x + slot.width / 2 +
            screenCornerOffsetX * Math.cos(contentRotationRad) +
            screenCornerOffsetY * Math.sin(contentRotationRad);
          const buttonY =
            slot.y + slot.height / 2 -
            screenCornerOffsetX * Math.sin(contentRotationRad) +
            screenCornerOffsetY * Math.cos(contentRotationRad);
          const childSelected = child.id === selectedChildId;
          const flyoutOpen = !interactionOnly && childSelected && flyout?.childId === child.id;
          const childDefinition = definition.items?.find(
            (candidate) => child.definition?.definitionId === candidate.id,
          );
          const currentPlugType = child.data.details?.plugType ?? C1_EloItem_PlugType.V230;
          const plugTypeOptions = (childDefinition?.details?.plugType?.options ?? []).filter(
            (plugType) => plugType !== currentPlugType,
          );
          return (
            <ConnectionEndpointGroup
              key={child.id}
              data-c1-equipment-id={child.id}
              object={!interactionOnly || currentPlugType.startsWith(C1_EloItem_PlugType.V230Switch) ? child.owner : undefined}
              anchorSelector="[data-c1-outlet-connection-anchor]"
              onPointerEnter={() => onSlotHoverChange?.(child.id)}
              onPointerLeave={() => onSlotHoverChange?.(undefined)}
            >
              <rect
                data-c1-outlet-connection-anchor=""
                x={slot.x}
                y={slot.y}
                width={slot.width}
                height={slot.height}
                fill="transparent"
                pointerEvents="all"
                role="button"
                aria-label={itx("c1.ui.Steckplatz_value1_auswahlen", "Steckplatz {{value1}} auswählen", { "{{value1}}": String(index + 1) })}
                tabIndex={0}
                onPointerDown={(event) => {
                  event.stopPropagation();
                  onSlotPointerDown?.(event, child);
                }}
                onClick={(event) => {
                  event.stopPropagation();
                  onSlotClick?.(child);
                }}
                onKeyDown={(event) => {
                  if (event.key !== "Enter" && event.key !== " ") return;
                  event.preventDefault();
                  event.stopPropagation();
                  onSlotClick?.(child);
                }}
              />
              {flyoutOpen && (
                ReactDOM.createPortal(
                  <div className="C1 C1_eloOverlayPortal">
                    <div
                      ref={flyoutRef}
                      className="C1_eloSlotTypeFlyout"
                      role="listbox"
                      style={{ left: flyout.left, top: flyout.top }}
                    >
                      {plugTypeOptions.map((plugType) => (
                        <button
                          key={plugType}
                          type="button"
                          className="C1_eloSlotTypeOption"
                          role="option"
                          aria-label={C1_ELO_PLUG_TYPE_LABELS[plugType]}
                          onPointerDown={(event) => event.stopPropagation()}
                          onClick={(event) => {
                            event.stopPropagation();
                            runTreeEdit(item, (root) => updateEloItemDetails(root, child.id, { plugType }), child.id);
                            setFlyout(undefined);
                          }}
                        >
                          <WallOutletSvg
                            slots={[plugTypeSlot(plugType)]}
                            slotLabels={[plugTypeLabel(plugType)]}
                            width="100%"
                            height="100%"
                          />
                        </button>
                      ))}
                    </div>
                  </div>,
                  document.getElementById("root") ?? document.body,
                )
              )}
              {!interactionOnly && childSelected && (
                <g
                  className={`C1_eloInlineAction C1_eloInlineAction--edit${flyoutOpen ? " C1_open" : ""}`}
                  role="button"
                  aria-label={`Steckplatz ${index + 1} bearbeiten`}
                  tabIndex={0}
                  transform={`translate(${buttonX} ${buttonY})`}
                  onPointerDown={stopPointer}
                  onClick={(event) => {
                    event.stopPropagation();
                    const rect = event.currentTarget.getBoundingClientRect();
                    setFlyout((current) => current?.childId === child.id
                      ? undefined
                      : {
                          childId: child.id,
                          anchor: event.currentTarget,
                          left: rect.left + rect.width / 2 - 18,
                          top: rect.bottom + 1,
                        });
                  }}
                >
                  <circle r={controlSize / 2} />
                  {flyoutOpen ? (
                    <path
                      d={`M ${-controlSize * 0.2} ${controlSize * 0.08} L 0 ${-controlSize * 0.16} L ${controlSize * 0.2} ${controlSize * 0.08}`}
                    />
                  ) : (
                    <path
                      d={`M ${-controlSize * 0.2} ${-controlSize * 0.08} L 0 ${controlSize * 0.16} L ${controlSize * 0.2} ${-controlSize * 0.08}`}
                    />
                  )}
                </g>
              )}
            </ConnectionEndpointGroup>
          );
        })}

        {showAddSlot && (
          <g
            className="C1_eloInlineAdd"
            role="button"
            aria-label={itx("c1.ui.value1_hinzufugen", "{{value1}} hinzufügen", { "{{value1}}": String(addItemName) })}
            tabIndex={0}
            onPointerEnter={() => { onAddHover?.(); setAddHovered(true); }}
            onPointerLeave={() => setAddHovered(false)}
            onFocus={() => { onAddHover?.(); setAddHovered(true); }}
            onBlur={() => setAddHovered(false)}
            onPointerDown={stopPointer}
            onClick={(event) => {
              event.stopPropagation();
              appendRepeatedItemAndSelect(item, definition);
            }}
          >
            <rect
              x={layout.nextSlot.x}
              y={layout.nextSlot.y}
              width={layout.nextSlot.width}
              height={layout.nextSlot.height}
              rx={Math.min(4, layout.nextSlot.width * 0.16)}
            />
            <path
              d={`M ${layout.nextSlot.x + layout.nextSlot.width * 0.28} ${layout.nextSlot.y + layout.nextSlot.height / 2} H ${layout.nextSlot.x + layout.nextSlot.width * 0.72} M ${layout.nextSlot.x + layout.nextSlot.width / 2} ${layout.nextSlot.y + layout.nextSlot.height * 0.28} V ${layout.nextSlot.y + layout.nextSlot.height * 0.72}`}
            />
            {addHovered && (
              <g className="C1_eloGhostAddBadge" transform={`translate(${addBadgeX} ${addBadgeY}) rotate(${-contentRotation})`} pointerEvents="all">
                <C1_EloAddBadge name={addItemName} />
              </g>
            )}
          </g>
        )}
      </g>

    </>
  );
}

type InlineEditorAdapter = {
  supports: (definition: C1_EloItem_Definition) => boolean;
  Component: (props: C1_EloInlineEditorProps) => JSX.Element;
};

const INLINE_EDITORS: InlineEditorAdapter[] = [
  {
    supports: (definition) =>
      definition.type === C1_EloItem_Type.PlugGroup && definition.details?.repeatCount !== undefined,
    Component: C1_PlugGroupInlineEditor,
  },
];

export function C1_EloInlineEditor(props: C1_EloInlineEditorProps) {
  const adapter = INLINE_EDITORS.find((candidate) => candidate.supports(props.definition));
  return adapter ? <adapter.Component {...props} /> : null;
}
